import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

interface Props {
  children: ReactNode;
  title: string;
  subtitle?: string;
  illustration?: ReactNode;
  footer?: ReactNode;
}

/**
 * Two-column layout shared by every auth page. Form lives on the
 * left (so the focus lands there immediately), brand illustration
 * on the right (collapses on mobile so the form takes the viewport).
 */
export function AuthLayout({ children, title, subtitle, illustration, footer }: Props) {
  const { t } = useTranslation();
  return (
    <div className="relative isolate min-h-screen overflow-hidden">
      {/* Background — same blob aesthetic as the landing hero. */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 gradient-mesh" />
        <motion.div
          className="absolute -top-32 -start-32 h-[28rem] w-[28rem] rounded-full bg-gradient-to-br from-indigo-400/35 to-fuchsia-400/30 blur-3xl"
          animate={{ y: [0, -20, 0], x: [0, 15, 0] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute -bottom-32 -end-32 h-[28rem] w-[28rem] rounded-full bg-gradient-to-br from-violet-300/30 to-indigo-300/30 blur-3xl"
          animate={{ y: [0, 20, 0], x: [0, -15, 0] }}
          transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>

      {/* Top-left brand chip — discreet way home that doesn't compete
          with the form. Same logo + wordmark treatment as SiteHeader,
          wrapped in a glass chip so it reads as context, not navigation. */}
      <Link
        to="/"
        aria-label={t('brand.home_aria', { defaultValue: 'KeenEZ — back to home' })}
        className="absolute top-5 start-5 z-10 inline-flex items-center gap-2 rounded-full glass-strong px-3 py-1.5 shadow-soft hover:shadow-glow transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        <BrandMark />
        <span className="font-display text-lg font-semibold tracking-tight">{t('brand.name')}</span>
      </Link>

      <div className="relative grid min-h-screen lg:grid-cols-2">
        {/* Form column */}
        <div className="flex items-center justify-center px-4 py-20 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 0.84, 0.44, 1] as const }}
            className="w-full max-w-md"
          >
            <h1 className="display serif-jp text-3xl md:text-4xl font-semibold tracking-tight text-balance">{title}</h1>
            {subtitle && <p className="mt-2 text-sm text-muted-foreground text-pretty">{subtitle}</p>}
            <div className="mt-8">{children}</div>
            {footer && <div className="mt-6 text-sm text-muted-foreground">{footer}</div>}
          </motion.div>
        </div>

        {/* Illustration column — hidden on mobile so the form fits. */}
        <div className="hidden lg:flex relative items-center justify-center px-8 py-12 border-s border-border/40 bg-background/30 backdrop-blur-sm">
          {illustration ?? <DefaultIllustration />}
        </div>
      </div>
    </div>
  );
}

function BrandMark() {
  return (
    <img
      src="/logo.png"
      alt="KeenEZ"
      className="h-9 w-9 rounded-xl object-contain"
      width={36}
      height={36}
    />
  );
}

function DefaultIllustration() {
  const { t } = useTranslation();
  return (
    <div className="relative w-full max-w-md">
      {/* Stacked floating cards — feels like a real product surface,
          not a decorative blob. Each tile rotates softly. */}
      <motion.div
        animate={{ y: [0, -10, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
        className="rounded-3xl glass-strong shadow-card-lift p-6"
      >
        <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">{t('hero.eyebrow')}</p>
        <p className="display serif-jp mt-2 text-2xl font-semibold leading-tight">
          {t('hero.headline_part1')} <span className="gradient-text">{t('hero.rotate_cross_border')}</span> {t('hero.headline_part2')}
        </p>
        <div className="mt-5 grid grid-cols-3 gap-2 text-center">
          {[
            { v: '14', l: t('hero.stat_languages') },
            { v: '4',  l: t('hero.stat_payrolls') },
            { v: '30m', l: t('hero.stat_time_to_run') },
          ].map((s) => (
            <div key={s.l} className="rounded-xl bg-muted/60 px-2 py-3">
              <p className="num text-xl font-bold gradient-text">{s.v}</p>
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground mt-1">{s.l}</p>
            </div>
          ))}
        </div>
      </motion.div>

      <motion.div
        animate={{ y: [0, 8, 0], rotate: [-2, -2, -2] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute -start-6 -bottom-6 rounded-2xl glass-strong shadow-soft px-4 py-3 flex items-center gap-3"
      >
        <span className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-white">✦</span>
        <div>
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Border Buddy</p>
          <p className="text-xs font-semibold">JP–DE Art. 7</p>
        </div>
      </motion.div>

      <motion.div
        animate={{ y: [0, -8, 0], rotate: [2, 2, 2] }}
        transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut', delay: 0.4 }}
        className="absolute -end-6 -top-6 rounded-2xl glass-strong shadow-soft px-4 py-3 flex items-center gap-2"
      >
        <span className="text-base">🇯🇵</span>
        <span className="num text-xs font-semibold">¥348,284</span>
      </motion.div>
    </div>
  );
}
